import { useRef, useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Image as ImageIcon, Paperclip, Star, Trash2, Upload } from 'lucide-react'
import Card from '@/components/layout/Card'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { formatShortDate } from '@/lib/dealFormat'
import {
  attachmentUrl,
  deleteAttachment,
  listAttachments,
  setDealCover,
  uploadAttachment,
  type Attachment,
  type AttachmentTarget,
} from '@/api/attachments'

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Files on a deal or a contact — a signed agreement, photos, an ID — as in HubSpot's Attachments card:
 * anyone who works the record uploads (several at once), opens or removes them. No set-up needed
 * (unlike a "File" custom field, which an admin had to create first).
 */
export default function AttachmentsCard({
  target,
  collapsible,
  coverAttachmentId,
}: {
  target: AttachmentTarget
  collapsible?: boolean
  /** On a deal: which photo is its cover — a star on each photo picks it. */
  coverAttachmentId?: string | null
}) {
  const qc = useQueryClient()
  const key = ['attachments', target.kind, target.id]
  const isDeal = target.kind === 'deal'
  // A deal's photo shows on its card and header: refresh those when files or the cover change.
  const refreshDeal = () => {
    if (!isDeal) return
    qc.invalidateQueries({ queryKey: ['deal', target.id] })
    qc.invalidateQueries({ queryKey: ['deals'] })
  }
  const cover = useMutation({
    mutationFn: (attachmentId: string | null) => setDealCover(target.id, attachmentId),
    onSuccess: refreshDeal,
    onError: (e) => setError(apiErrorMessage(e)),
  })
  const { data: files } = useQuery({ queryKey: key, queryFn: () => listAttachments(target) })
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(0)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const remove = useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: () => {
      setConfirmId(null)
      qc.invalidateQueries({ queryKey: key })
      refreshDeal()
    },
    onError: (e) => setError(apiErrorMessage(e)),
  })

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = '' // the same file can be picked again after a failure
    if (picked.length === 0) return
    setError(null)
    setUploading(picked.length)
    for (const f of picked) {
      try {
        await uploadAttachment(target, f)
      } catch (err) {
        setError(`${f.name}: ${apiErrorMessage(err)}`)
      }
      setUploading((n) => n - 1)
    }
    qc.invalidateQueries({ queryKey: key })
    refreshDeal() // the first photo becomes the deal's cover
  }

  async function open(a: Attachment) {
    // Open the tab inside the click (a popup blocker would stop one opened after the await).
    const w = window.open('', '_blank')
    try {
      const url = await attachmentUrl(a.id)
      if (w) w.location.replace(url)
      else window.location.assign(url)
    } catch (err) {
      w?.close()
      setError(apiErrorMessage(err))
    }
  }

  const list = files ?? []
  return (
    <Card
      title={`Attachments${list.length ? ` · ${list.length}` : ''}`}
      collapsible={collapsible}
      action={
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading > 0}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-dark disabled:opacity-60"
        >
          <Upload size={13} /> {uploading > 0 ? `Uploading ${uploading}…` : 'Upload'}
        </button>
      }
    >
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} aria-label="Upload files" />
      {list.length === 0 && uploading === 0 && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-500 hover:border-brand-primary/40 hover:text-brand-primary"
        >
          <Paperclip size={16} className="mx-auto mb-1 text-gray-300" />
          {isDeal
            ? 'Add photos, a contract or any file (up to 10 MB each) — the first photo becomes the deal’s picture'
            : 'Add a contract, photos or any file (up to 10 MB each)'}
        </button>
      )}
      {list.length > 0 && (
        <ul className="space-y-1.5">
          {list.map((a) => (
            <li key={a.id} className="group flex items-center gap-2.5 text-sm">
              <span className="w-8 h-8 shrink-0 rounded-lg bg-gray-50 border border-gray-100 text-gray-400 flex items-center justify-center">
                {a.contentType?.startsWith('image/') ? <ImageIcon size={15} /> : <FileText size={15} />}
              </span>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => open(a)} className="block max-w-full truncate text-left font-medium text-brand-dark hover:text-brand-primary hover:underline" title={`Open ${a.filename}`}>
                  {a.filename}
                </button>
                <div className="text-[11px] text-gray-400 truncate">
                  {fileSize(a.size)} · {formatShortDate(a.createdAt)}{a.uploadedByName ? ` · ${a.uploadedByName}` : ''}
                </div>
              </div>
              {isDeal && a.contentType?.startsWith('image/') && confirmId !== a.id && (
                <button
                  type="button"
                  onClick={() => cover.mutate(coverAttachmentId === a.id ? null : a.id)}
                  disabled={cover.isPending}
                  title={coverAttachmentId === a.id ? 'The deal’s photo — click to stop using it' : 'Use as the deal’s photo (card, table and header)'}
                  aria-label={coverAttachmentId === a.id ? `Stop using ${a.filename} as the deal’s photo` : `Use ${a.filename} as the deal’s photo`}
                  aria-pressed={coverAttachmentId === a.id}
                  className={coverAttachmentId === a.id
                    ? 'p-1 shrink-0 text-amber-500'
                    : 'p-1 shrink-0 text-gray-300 hover:text-amber-500 opacity-60 group-hover:opacity-100'}
                >
                  <Star size={14} fill={coverAttachmentId === a.id ? 'currentColor' : 'none'} />
                </button>
              )}
              {confirmId === a.id ? (
                <span className="flex items-center gap-1 shrink-0 text-xs">
                  <button type="button" onClick={() => remove.mutate(a.id)} disabled={remove.isPending} className="px-2 py-1 rounded-md bg-danger text-white hover:opacity-90 disabled:opacity-60">
                    {remove.isPending ? 'Removing…' : 'Remove'}
                  </button>
                  <button type="button" onClick={() => setConfirmId(null)} className="px-1.5 py-1 text-gray-500 hover:text-brand-dark">Keep</button>
                </span>
              ) : (
                <button type="button" onClick={() => setConfirmId(a.id)} title="Remove file" aria-label={`Remove ${a.filename}`} className="p-1 shrink-0 text-gray-300 hover:text-danger opacity-60 group-hover:opacity-100">
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {isDeal && list.some((a) => a.contentType?.startsWith('image/')) && (
        <p className="mt-2 text-[11px] text-gray-400">
          <Star size={10} className="inline -mt-0.5 text-amber-500" fill="currentColor" /> marks the photo shown on the deal’s card — click a star to change it.
        </p>
      )}
      {error && <p className="text-danger text-xs mt-2">{error}</p>}
    </Card>
  )
}
